/**
 * Curator Bridge Slice-4 data access.
 *
 * Every call in this file is a `supabase.functions.invoke("curate_signal", ...)`.
 * The browser never reads or writes `signals`, `v_signal_feed_internal`,
 * `v_signal_curation_queue` or `signal_publication_decisions`, and never calls
 * `bdse_record_publication_decision`. It could not succeed if it tried - those
 * relations are revoked from `anon` and `authenticated`, and the decision
 * function is revoked from both as well - but routing through the Edge Function
 * is the design, not a fallback: that is where the capability is derived and the
 * transition matrix enforced.
 *
 * No function here generates a decision_key. `decision_key` is a required
 * parameter, supplied by the controller that owns the attempt, so the identical
 * call can be re-issued for a retry without silently becoming a second decision.
 */

import { supabase } from '@/auth/supabase-client';
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from '@/auth/session-guard';
import { toCuratorApiError } from './errors';
import {
  toCuratorQueuePage,
  toDecisionResult,
  toPublicationContext,
} from './normalize';
import type {
  CuratorQueuePage,
  DecisionResult,
  ListableQueueState,
  PublicationContext,
} from './types';
import type { DecisionIntent } from './decision-intent';

const FUNCTION_NAME = 'curate_signal';

/**
 * Page size for the queue.
 *
 * The server caps a positive integer limit at PENDING_PAGE_MAX (200) rather than
 * refusing it, so an over-large value would be silently clamped and the client
 * would believe it had asked for something it did not get. Sending a fixed,
 * bounded value well inside the cap keeps the request and the response honest,
 * and pagination is by keyset cursor regardless of page size.
 */
export const CURATOR_PAGE_SIZE = 25;

/**
 * The one network call.
 *
 * Both failure paths funnel into `toCuratorApiError`, so every caller receives a
 * classified CuratorApiError and no caller has to know that a timeout throws
 * while an HTTP error is returned:
 *   - `withSupabaseTimeout` THROWS SupabaseTimeoutError when its timer wins;
 *   - `invoke` RESOLVES `{ data, error }` for every transport and HTTP failure.
 */
async function invokeCurator(body: Record<string, unknown>): Promise<unknown> {
  let result: { data: unknown; error: unknown };
  try {
    await ensureFreshSession();
    result = await withSupabaseTimeout(
      supabase.functions.invoke(FUNCTION_NAME, { body }),
      `${FUNCTION_NAME} ${String(body.operation)}`,
      SUPABASE_EDGE_TIMEOUT_MS,
    );
  } catch (thrown) {
    throw await toCuratorApiError(thrown);
  }
  if (result.error) throw await toCuratorApiError(result.error);

  // successResponse wraps every payload as { data: ... }.
  const payload = result.data;
  if (payload !== null && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: unknown }).data;
  }
  return payload;
}

/**
 * One page of withheld signals in a single state.
 *
 * The server lists exactly one publication_state per request - `pending_review`
 * by default, `rejected` on request - and never returns published signals. The
 * caller therefore asks for one state at a time rather than receiving a mixed
 * queue, which is why the UI presents the two as separate views.
 */
export async function listCuratorQueue(params: {
  publicationState: ListableQueueState;
  cursor?: string | null;
  limit?: number;
}): Promise<CuratorQueuePage> {
  const body: Record<string, unknown> = {
    operation: 'list_pending',
    publication_state: params.publicationState,
    limit: params.limit ?? CURATOR_PAGE_SIZE,
  };
  if (params.cursor) body.cursor = params.cursor;
  const data = await invokeCurator(body);
  return toCuratorQueuePage(data, params.publicationState);
}

/**
 * The expected state and version for a signal that is currently published.
 *
 * Called only when a curator initiates an unpublish, never on modal open: it is
 * a decision prerequisite, not detail hydration, and an ordinary user viewing
 * the same signal must not trigger it at all.
 *
 * Throws a 404-classified CuratorApiError when the signal is not published,
 * which is the honest answer if it was withdrawn between page load and click.
 */
export async function getPublicationContext(
  signalId: string,
): Promise<PublicationContext> {
  const data = await invokeCurator({
    operation: 'get_publication_context',
    signal_id: signalId,
  });
  const context = toPublicationContext(data);
  if (!context) {
    throw await toCuratorApiError(
      new Error('The signal is no longer published. Refresh and try again.'),
    );
  }
  return context;
}

/**
 * Records one decision.
 *
 * `decisionKey` is a parameter and never minted here: the caller owns the
 * attempt, so re-issuing this exact call is a retry of the same decision rather
 * than a new one. `expectedPublicationVersion` is passed through as the string
 * the server issued, never reformatted or parsed - it is a bigint, and a
 * round-trip through a JavaScript number would corrupt it beyond 2^53.
 */
export async function submitCuratorDecision(
  intent: DecisionIntent,
  decisionKey: string,
): Promise<DecisionResult> {
  const body: Record<string, unknown> = {
    operation: intent.kind,
    signal_id: intent.signalId,
    decision_key: decisionKey,
    expected_publication_state: intent.expectedPublicationState,
    expected_publication_version: intent.expectedPublicationVersion,
  };
  if (intent.reason !== null) body.reason = intent.reason;
  if (Object.keys(intent.metadata).length > 0) body.metadata = intent.metadata;
  const data = await invokeCurator(body);
  const result = toDecisionResult(data);
  if (!result) {
    // The request may well have succeeded on the server - this says only that
    // the response cannot be understood. Failing here keeps the UI from
    // reporting "Decision recorded" on the strength of a payload it could not
    // read; the curator re-reads the queue and sees the real current state.
    throw await toCuratorApiError(
      new Error(
        'The decision response could not be read. Refresh the queue to see the current state.',
      ),
    );
  }
  return result;
}
